import { useState } from "react";
import { IonContent, IonPage } from "@ionic/react";

import { ActionBar } from "../components/ActionBar";
import { BarChart } from "../components/BarChart";
import { Hero } from "../components/Hero";
import { I } from "../components/icons";
import { ProgressBar } from "../components/ProgressBar";
import { DiscloseRow, NavRow, RowGroup } from "../components/Rows";
import { Sheet } from "../components/Sheet";
import { Screen } from "../components/Shell";
import { StepsRefresh } from "../components/StepsRefresh";
import { Tag } from "../components/Tag";
import { TopBar } from "../components/TopBar";
import { average, hourlySteps, recentSteps, rings } from "../domain/activity";
import { DOW_LABELS, dayLabel, parseDay, todayStr } from "../domain/date";
import { stepBreakdown } from "../domain/energy";
import { workParts } from "../domain/today";
import { STEP_BASIS_SENTENCE, stepTone, workSentence } from "../domain/todayView";
import { useKaya } from "../store/kayaStore";
import "./Activity.css";

/**
 * A count typed by hand for a day the phone missed. It writes `logs[ds].steps`, never
 * `deviceSteps`: a phone automation must never overwrite a hand count, and `stepBreakdown` is the
 * only place the two meet (invariant 6). Moved here from Progress's weigh-in sheet.
 */
function ManualStepsSheet({ onClose }: { onClose: () => void }) {
  const { S, update, showToast, checkBadges } = useKaya();
  const today = todayStr();
  const [ds, setDs] = useState(today);
  const [val, setVal] = useState(() => (S.logs[today]?.steps ? String(S.logs[today].steps) : ""));

  const L = S.logs[ds];
  const phone = L?.deviceSteps || 0;

  const pickDay = (next: string) => {
    setDs(next);
    setVal(S.logs[next]?.steps ? String(S.logs[next].steps) : "");
  };

  const save = () => {
    const n = parseInt(val, 10);
    if (!ds || ds > today || isNaN(n) || n < 0) {
      showToast("Enter a day up to today and a count");
      return;
    }
    update((d) => {
      if (!d.logs[ds]) d.logs[ds] = { done: {}, treadmill: null };
      d.logs[ds].steps = n;
    });
    showToast("Saved");
    checkBadges();
    onClose();
  };

  return (
    <Sheet
      title="Add steps by hand"
      sub="For a day the phone missed. Your phone's own count is kept apart."
      onClose={onClose}
      footer={<ActionBar inSheet primary={{ label: "Save steps", onClick: save }} secondary={{ label: "Cancel", onClick: onClose }} />}
    >
      <div className="field">
        <label htmlFor="act-steps-day">Day</label>
        <input id="act-steps-day" type="date" max={today} value={ds} onChange={(e) => pickDay(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="act-steps-n">Steps</label>
        <input
          id="act-steps-n"
          type="number"
          step="1"
          min="0"
          inputMode="numeric"
          value={val}
          onChange={(e) => setVal(e.target.value)}
        />
      </div>
      <p className="k-sheet__sub">
        {phone
          ? `Your phone counted ${phone.toLocaleString()} on ${dayLabel(ds)}; the higher figure is the one used.`
          : `No count from your phone on ${dayLabel(ds)}.`}
      </p>
    </Sheet>
  );
}

/**
 * Today › Activity: the day's steps in full — source, refresh, by hour, the week around it —
 * then the three goals as bars, the work behind the Work figure, and a hand count for a day the
 * phone missed (audit §4). Everything "over time" beyond seven days belongs to Progress.
 */
export default function Activity() {
  const { S } = useKaya();
  const [manual, setManual] = useState(false);

  const ds = todayStr();
  const steps = stepBreakdown(S, ds);
  const counted = steps.basis === "none" ? null : steps.total;
  const tone = stepTone(steps.basis);
  const r = rings(S, ds);
  const goal = r.steps.goal;
  const work = workParts(S, ds);
  const sentence = workSentence(work);
  const moveKnown = work.total !== null || r.move.value > 0;

  const hours = hourlySteps(S.sampleCache?.date === ds ? S.sampleCache.samples : []);
  const hourNow = new Date().getHours();
  const hourTotal = hours.reduce((a, n) => a + n, 0);
  const week = recentSteps(S, ds);
  const weekAvg = average(week);

  const toGo = counted === null ? null : Math.max(0, goal - counted);
  const met = (v: number | null, g: number) => (v !== null && v >= g ? " · met" : "");

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <TopBar title="Activity" back={{ to: "/today", label: "Back to Today" }} />
          <div className="activity">
            <Hero
              ariaLabel="Steps today"
              label="Steps today"
              figure={counted}
              unit={<>steps {tone ? <Tag tone={tone} /> : null}</>}
              line={
                counted === null
                  ? `Nothing counted yet · goal ${goal.toLocaleString()}`
                  : toGo
                    ? `of ${goal.toLocaleString()} goal · ${toGo.toLocaleString()} to go`
                    : `of ${goal.toLocaleString()} goal · met`
              }
              visual={
                <>
                  <ProgressBar
                    value={counted}
                    max={goal}
                    ariaLabel={
                      counted === null
                        ? "No steps counted yet"
                        : `${counted.toLocaleString()} steps of a ${goal.toLocaleString()} goal`
                    }
                  />
                  <StepsRefresh compact />
                </>
              }
            />

            <section className="activity-chart" aria-label="Steps by hour">
              <div className="activity-chart__head">
                <h2 className="k-group__h">Steps by hour</h2>
                {hours[hourNow] ? (
                  <span className="activity-chart__value">{hours[hourNow].toLocaleString()} this hour</span>
                ) : null}
              </div>
              <div className="activity-panel">
                <BarChart
                  mode="dense"
                  values={hours.map((n) => (n > 0 ? n : null))}
                  current={hourNow}
                  unit="steps"
                  ariaLabel={`Steps by hour today, ${hourTotal.toLocaleString()} so far${
                    hours[hourNow] ? `; ${hours[hourNow].toLocaleString()} in the ${String(hourNow).padStart(2, "0")}:00 hour` : ""
                  }`}
                  empty="The hourly shape fills in as your phone reports through the day."
                />
              </div>
            </section>

            <section className="activity-chart" aria-label="Last 7 days">
              <div className="activity-chart__head">
                <h2 className="k-group__h">Last 7 days</h2>
                <span className="activity-chart__value">
                  {weekAvg ? `avg ${weekAvg.toLocaleString()} a day` : "Not enough days yet"}
                </span>
              </div>
              <div className="activity-panel">
                <BarChart
                  mode="single"
                  bars={week.map((w) => ({
                    key: w.date,
                    label: DOW_LABELS[parseDay(w.date).getDay()],
                    name: dayLabel(w.date),
                    value: w.steps > 0 ? w.steps : null,
                    current: w.date === ds,
                  }))}
                  avg={weekAvg || null}
                  unit="steps"
                  readout={(b) =>
                    `${b.name} · ${(b.value ?? 0).toLocaleString()} steps${
                      b.key === ds ? " so far" : ""
                    }${(b.value ?? 0) >= goal ? " · goal met" : ""}`
                  }
                  ariaLabel={`Steps, last 7 days.${counted !== null ? ` Today ${counted.toLocaleString()} so far.` : ""}${
                    weekAvg ? ` Average ${weekAvg.toLocaleString()} a day.` : ""
                  }`}
                  empty="No steps counted this week yet."
                />
                <div className="activity-panel__row">
                  <NavRow icon={I.list} title="Every day's steps" to="/progress/records/steps" />
                </div>
              </div>
            </section>

            <section className="activity-chart" aria-label="Goals">
              <h2 className="k-group__h">Goals</h2>
              <div className="activity-panel activity-panel--pad">
                <ProgressBar
                  value={counted}
                  max={goal}
                  label={counted === null ? "– steps" : `${counted.toLocaleString()} steps`}
                  detail={`goal ${goal.toLocaleString()}${met(counted, goal)}`}
                  ariaLabel={counted === null ? "No steps counted yet" : `${counted.toLocaleString()} steps of a ${goal.toLocaleString()} goal`}
                />
                <ProgressBar
                  value={work.total}
                  max={r.kcal.goal}
                  label={work.total === null ? "– work kcal" : `~${work.total.toLocaleString()} work kcal`}
                  detail={`goal ${r.kcal.goal.toLocaleString()}${met(work.total, r.kcal.goal)}`}
                  ariaLabel={
                    work.total === null
                      ? "No work logged yet"
                      : `About ${work.total.toLocaleString()} work kcal of a ${r.kcal.goal.toLocaleString()} goal`
                  }
                />
                <ProgressBar
                  value={moveKnown ? r.move.value : null}
                  max={r.move.goal}
                  label={moveKnown ? `${r.move.value} move min` : "– move min"}
                  detail={`goal ${r.move.goal}${met(moveKnown ? r.move.value : null, r.move.goal)}`}
                  ariaLabel={
                    moveKnown ? `${r.move.value} move minutes of a ${r.move.goal} minute goal` : "No movement logged yet"
                  }
                />
              </div>
            </section>

            <RowGroup heading="Details">
              <NavRow
                kind="value"
                title="Work today"
                subtitle={
                  sentence
                    ? `${sentence} Work done, not what your body spends at rest.`
                    : "Nothing logged yet. Sessions, the treadmill and walks add up here."
                }
                icon={I.flame}
                tone="brand"
              />
              <DiscloseRow title="Where the count comes from" icon={I.phone} tone="sky" defaultOpen>
                {STEP_BASIS_SENTENCE[steps.basis]}
              </DiscloseRow>
              <NavRow
                title="Add steps by hand"
                subtitle="For a day the phone missed"
                icon={I.edit}
                onClick={() => setManual(true)}
              />
            </RowGroup>
          </div>
        </Screen>
        {manual ? <ManualStepsSheet onClose={() => setManual(false)} /> : null}
      </IonContent>
    </IonPage>
  );
}
